import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CaretDown, CaretRight } from '@phosphor-icons/react';

import { listTeams, type Team } from '../api/teams';
import { listMembers, type Member } from '../api/members';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';
import type { BroadcastAudience } from '../api/broadcasts';

// RecipientPicker is the audience selector used by the broadcast editor.
// Three modes that combine in any way the admin wants:
//
//   - "Alla medlemmar" master toggle. When on, the rest is collapsed
//     and ignored at send time — the server resolves everyone in the org.
//   - Per-team checkbox. Picks the whole team's roster.
//   - Per-team expand → individual member checkboxes inside the team.
//     Selecting some members of a team WITHOUT the team-header check
//     means "just these specific members", not the whole team.
//
// The component is fully controlled — parent owns the BroadcastAudience
// shape `{ all_members, team_ids, member_ids }` and just renders / emits
// changes via onChange.

interface Props {
  slug: string;
  token: string | null;
  value: BroadcastAudience;
  onChange: (next: BroadcastAudience) => void;
  disabled?: boolean;
}

export function RecipientPicker({ slug, token, value, onChange, disabled }: Props) {
  const { t } = useTranslation('common');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Free-text member search across the whole org. When active it replaces
  // the team list with a flat, selectable result list — selections still
  // land in `member_ids`, exactly as ticking a member inside an expanded
  // team does. Debounced so a fast typist doesn't fire a request per key.
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const searching = debouncedQuery.length >= 2;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listTeams(token, slug);
        if (!cancelled) setTeams(res.teams ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const teamIDs = useMemo(() => new Set(value.team_ids ?? []), [value.team_ids]);
  const memberIDs = useMemo(() => new Set(value.member_ids ?? []), [value.member_ids]);

  function setAllMembers(on: boolean) {
    onChange(on ? { all_members: true } : {});
  }

  function toggleTeam(teamID: string, on: boolean) {
    const next = new Set(teamIDs);
    if (on) {
      next.add(teamID);
    } else {
      next.delete(teamID);
    }
    // Omit all_members entirely instead of setting it to undefined —
    // exactOptionalPropertyTypes rejects explicit-undefined on optional
    // fields.
    const out: BroadcastAudience = { team_ids: Array.from(next) };
    if (value.member_ids && value.member_ids.length > 0) {
      out.member_ids = value.member_ids;
    }
    onChange(out);
  }

  function toggleMember(memberID: string, on: boolean) {
    const next = new Set(memberIDs);
    if (on) {
      next.add(memberID);
    } else {
      next.delete(memberID);
    }
    const out: BroadcastAudience = { member_ids: Array.from(next) };
    if (value.team_ids && value.team_ids.length > 0) {
      out.team_ids = value.team_ids;
    }
    onChange(out);
  }

  function toggleExpand(teamID: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(teamID)) next.delete(teamID);
      else next.add(teamID);
      return next;
    });
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">{t('broadcast.recipients.loading', { defaultValue: 'Loading teams…' })}</p>;
  }

  const allOn = !!value.all_members;

  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-start gap-3 rounded-xl border border-border bg-background p-3">
        <Checkbox
          checked={allOn}
          onChange={(e) => setAllMembers(e.target.checked)}
          disabled={disabled}
          className="mt-0.5"
        />
        <span className="flex flex-col gap-0.5">
          <span className="font-heading text-sm font-semibold">
            {t('broadcast.recipients.allMembers', { defaultValue: 'All members' })}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('broadcast.recipients.allMembersHint', {
              defaultValue: 'Everyone in the organization who has an email address.',
            })}
          </span>
        </span>
      </label>

      {!allOn && (
        <>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('broadcast.recipients.searchPlaceholder', {
              defaultValue: 'Search members by name or email…',
            })}
            clearLabel={t('common.clearSearch')}
            disabled={disabled}
          />
          {searching ? (
            <MemberSearchResults
              slug={slug}
              token={token}
              query={debouncedQuery}
              memberIDs={memberIDs}
              disabled={disabled ?? false}
              onToggleMember={toggleMember}
            />
          ) : (
            <ul
              className={cn(
                'flex flex-col divide-y divide-border rounded-xl border border-border',
                disabled && 'opacity-60',
              )}
            >
              {teams.length === 0 ? (
                <li className="p-3 text-sm text-muted-foreground">
                  {t('broadcast.recipients.noTeams', { defaultValue: 'No teams yet.' })}
                </li>
              ) : (
                teams.map((team) => (
                  <TeamRow
                    key={team.id}
                    team={team}
                    slug={slug}
                    token={token}
                    expanded={expanded.has(team.id)}
                    teamSelected={teamIDs.has(team.id)}
                    memberIDs={memberIDs}
                    disabled={disabled ?? false}
                    onToggleTeam={(on) => toggleTeam(team.id, on)}
                    onToggleMember={toggleMember}
                    onToggleExpand={() => toggleExpand(team.id)}
                  />
                ))
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function TeamRow({
  team,
  slug,
  token,
  expanded,
  teamSelected,
  memberIDs,
  disabled,
  onToggleTeam,
  onToggleMember,
  onToggleExpand,
}: {
  team: Team;
  slug: string;
  token: string | null;
  expanded: boolean;
  teamSelected: boolean;
  memberIDs: Set<string>;
  disabled: boolean;
  onToggleTeam: (on: boolean) => void;
  onToggleMember: (memberID: string, on: boolean) => void;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation('common');
  const [members, setMembers] = useState<Member[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Lazy-load members on first expand. Keeps the picker snappy when the
  // org has many teams and the admin only opens one.
  useEffect(() => {
    if (!expanded || members !== null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await listMembers(token, slug, { teamIds: [team.id], limit: 200, hasEmail: true });
        if (!cancelled) setMembers(res.members ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, members, slug, team.id, token]);

  // How many of this team's members are selected, for the "4/34" badge +
  // the header checkbox's half-state. A whole-team selection (team in
  // team_ids) counts as all; otherwise we count individually-picked
  // members. The badge always renders as `selected/total` (e.g. "0/34"
  // before anything is picked) for a consistent column. Individual
  // selection requires expanding the team (which loads the roster), so a
  // never-expanded team correctly reads 0 selected.
  const selectedInTeam = teamSelected
    ? team.member_count
    : (members ?? []).filter((m) => memberIDs.has(m.id)).length;
  const partiallySelected =
    !teamSelected && selectedInTeam > 0 && selectedInTeam < team.member_count;

  return (
    <li className="flex flex-col">
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={t('broadcast.recipients.toggleTeam', {
            team: team.name,
            defaultValue: 'Toggle {{team}}',
          })}
          className="flex h-6 w-6 items-center justify-center rounded-pill text-foreground/70 hover:bg-muted"
        >
          {expanded ? <CaretDown className="h-3.5 w-3.5" /> : <CaretRight className="h-3.5 w-3.5" />}
        </button>
        <Checkbox
          checked={teamSelected}
          indeterminate={partiallySelected}
          onChange={(e) => onToggleTeam(e.target.checked)}
          disabled={disabled}
        />
        <span className="flex-1 truncate font-heading text-sm font-semibold">{team.name}</span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {selectedInTeam}/{team.member_count}
        </span>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/30 px-3 py-2">
          {loading ? (
            <p className="py-2 text-xs text-muted-foreground">
              {t('broadcast.recipients.loadingMembers', { defaultValue: 'Loading members…' })}
            </p>
          ) : members && members.length > 0 ? (
            <ul className="flex flex-col">
              {members.map((m) => (
                <li key={m.id} className="flex items-center gap-2 py-1.5">
                  <Checkbox
                    checked={memberIDs.has(m.id)}
                    onChange={(e) => onToggleMember(m.id, e.target.checked)}
                    disabled={disabled || teamSelected}
                  />
                  <span className="flex-1 truncate text-sm">
                    {m.first_name} {m.last_name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{m.email}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-2 text-xs text-muted-foreground">
              {t('broadcast.recipients.noMembersInTeam', {
                defaultValue: 'No members with email addresses in this team.',
              })}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// MemberSearchResults renders the flat, org-wide result list shown while a
// search query is active. Members tick straight into `member_ids` via the
// same toggleMember the team accordion uses, so a search-picked member and
// a team-expanded one are indistinguishable downstream. `hasEmail: true`
// mirrors the team loader — broadcasts only reach members with an address.
function MemberSearchResults({
  slug,
  token,
  query,
  memberIDs,
  disabled,
  onToggleMember,
}: {
  slug: string;
  token: string | null;
  query: string;
  memberIDs: Set<string>;
  disabled: boolean;
  onToggleMember: (memberID: string, on: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useQuery({
    queryKey: ['recipient-search', slug, query],
    queryFn: () => listMembers(token, slug, { search: query, hasEmail: true, limit: 50 }),
    enabled: !!slug && query.length > 0,
  });
  const members = data?.members ?? [];

  if (isLoading) {
    return (
      <p className="rounded-xl border border-border p-3 text-sm text-muted-foreground">
        {t('broadcast.recipients.loadingMembers', { defaultValue: 'Loading members…' })}
      </p>
    );
  }
  if (members.length === 0) {
    return (
      <p className="rounded-xl border border-border p-3 text-sm text-muted-foreground">
        {t('broadcast.recipients.searchEmpty', { defaultValue: 'No members match your search.' })}
      </p>
    );
  }
  return (
    <ul
      className={cn(
        'flex max-h-72 flex-col divide-y divide-border overflow-y-auto rounded-xl border border-border',
        disabled && 'opacity-60',
      )}
    >
      {members.map((m) => (
        <li key={m.id} className="flex items-center gap-2 p-3">
          <Checkbox
            checked={memberIDs.has(m.id)}
            onChange={(e) => onToggleMember(m.id, e.target.checked)}
            disabled={disabled}
          />
          <span className="flex-1 truncate text-sm">
            {m.first_name} {m.last_name}
          </span>
          <span className="truncate text-xs text-muted-foreground">{m.email}</span>
        </li>
      ))}
    </ul>
  );
}

// useDebouncedValue returns `value` after it has stopped changing for
// `delayMs`. Keeps the member-search query from firing a request on every
// keystroke.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
